import { FC, memo } from "react";
import { Button } from "../../../common/components/Button/Button";
import { useGlobalModalContext } from "../../../common/components/Modal/GlobalModal";
import { Title } from "../../../common/components/Title/Title";
import { MODAL_TYPES } from "../../../common/utils/consts";
import { AppealsTabs } from "./AppealsTabs";
import { OrdersTabs } from "./OrdersTabs";
import { SupportChat } from "../support/SupportChat";
import { UserInformation } from "../../../common/components/User/UserInformation";
import {
  useCloseSupportTicketMutation,
  useGetSupportTicketQuery,
  useGetSupportTicketsQuery,
  useReopenSupportTicketMutation,
} from "../../../services/supportApi";
import { useGetOrderByIdQuery } from "../../../services/orderApi";
import { useGetUserByIdQuery } from "../../../services/userApi";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { InformationBlock } from "../../../common/components/InformationBlock/InformationBlock";
import { QueryLayout } from "../../../common/components/Layout/QueryLayout";
import { Tabs } from "../../../common/components/Tabs/Tabs";
import { OrderItemDetails } from "../../../common/components/Order/OrderItemDetails";
import {
  DESKTOP_SECTION,
  DesktopSection,
  setActiveAppealId,
  setDesktopSection,
} from "./desktopSlice";
import { withErrorBoundary } from "common/utils/hoc/withErrorBoundary";

const getSectionTabs = (unreadCount: number) => [
  { value: DESKTOP_SECTION.ORDERS, label: "Заказы" },
  {
    value: DESKTOP_SECTION.APPEALS,
    label: unreadCount ? `Обращения · ${unreadCount}` : "Обращения",
  },
];

export const DesktopPage: FC = withErrorBoundary(
  memo(() => {
    const section = useAppSelector(
      (state) => state.platformManager.desktop.section
    );
    const activeAppealId = useAppSelector(
      (state) => state.platformManager.desktop.activeAppealId
    );
    const activeOrderId = useAppSelector(
      (state) => state.platformManager.desktop.activeOrderId
    );

    const isAppealsSection = section === DESKTOP_SECTION.APPEALS;

    const {
      data: appeal,
      isLoading: isAppealLoading,
      isFetching: isAppealFetching,
      error: appealError,
      refetch,
    } = useGetSupportTicketQuery(activeAppealId || -1, {
      skip: !isAppealsSection || !activeAppealId,
    });

    // Сообщений, ожидающих ответа, — на вкладке «Обращения» (тот же запрос, что в боковой панели)
    const { data: supportSummary } = useGetSupportTicketsQuery({
      status: "active",
      perPage: 1,
    });
    const [closeTicket, { isLoading: isClosingTicket }] =
      useCloseSupportTicketMutation();
    const [reopenTicket, { isLoading: isReopeningTicket }] =
      useReopenSupportTicketMutation();

    const { data: order } = useGetOrderByIdQuery(
      { id: activeOrderId || -1 },
      { skip: isAppealsSection || !activeOrderId }
    );

    // Заказчик выбранного заказа: заказ отдаёт только аккаунт, а блокировка и
    // баллы работают по пользователю, поэтому дотягиваем его отдельно
    const orderAccountId = order?.account?.id;
    const { data: orderUser, refetch: refetchOrderUser } = useGetUserByIdQuery(
      orderAccountId || -1,
      { skip: isAppealsSection || !orderAccountId }
    );

    const dispatch = useAppDispatch();

    const handleSetSection = (value: DesktopSection) => {
      dispatch(setDesktopSection(value));
    };

    // Раньше кнопка лишь снимала выделение, обращение так и оставалось активным
    const handleCloseAppeal = async () => {
      if (!activeAppealId) return;
      const result = await closeTicket(activeAppealId);
      if (!("error" in result)) {
        dispatch(setActiveAppealId(undefined));
      }
    };

    const handleReopenAppeal = () => {
      if (activeAppealId) reopenTicket(activeAppealId);
    };

    const { showModal } = useGlobalModalContext();

    const showBanModal = () => {
      showModal(MODAL_TYPES.BAN_MODAL, {
        userId: appeal?.user?.id,
        isBanned: appeal?.user?.is_banned,
        unbannedAt: new Date(),
        onConfirm: () => refetch(),
      });
    };

    const showBonusesModal = () => {
      showModal(MODAL_TYPES.BONUSES_MODAL, {
        userId: appeal?.user?.id,
      });
    };

    const showOrderUserBanModal = () => {
      showModal(MODAL_TYPES.BAN_MODAL, {
        userId: orderUser?.id,
        isBanned: orderUser?.is_banned,
        unbannedAt: new Date(),
        onConfirm: () => refetchOrderUser(),
      });
    };

    const showOrderUserBonusesModal = () => {
      showModal(MODAL_TYPES.BONUSES_MODAL, {
        userId: orderUser?.id,
      });
    };

    const showReportModal = () => {
      showModal(MODAL_TYPES.REPORT_MODAL);
    };

    return (
      <>
        <div
          className={`grid gap-[10px] overflow-y-hidden ${
            isAppealsSection
              ? "grid-rows-[35px,35px,40px,1fr]"
              : "grid-rows-[35px,40px,1fr]"
          }`}
        >
          <Tabs
            tabs={getSectionTabs(supportSummary?.unread_total ?? 0)}
            value={section}
            onTabClick={handleSetSection}
          />
          {isAppealsSection ? <AppealsTabs /> : <OrdersTabs />}
        </div>
        {isAppealsSection ? (
          <QueryLayout
            isLoading={isAppealLoading}
            isFetching={isAppealFetching}
            error={appealError}
          >
            {appeal && activeAppealId ? (
              <>
                <div className="grid grid-rows-[40px,minmax(200px,1fr),75px] gap-[10px] overflow-y-hidden">
                  <Title className="flex items-center">
                    Чат с пользователем
                  </Title>
                  <SupportChat
                    key={appeal.id}
                    ticketId={appeal.id}
                    isFinished={appeal.is_finished}
                  />
                </div>
                <div className="grid grid-rows-[40px,90px,1fr,225px] gap-[10px] overflow-y-hidden">
                  <Title className="flex items-center">
                    Информация о пользователе
                  </Title>
                  {appeal.user?.account_id ? (
                    <UserInformation userId={appeal.user.account_id} />
                  ) : (
                    <InformationBlock>
                      У обращения нет привязанного пользователя
                    </InformationBlock>
                  )}
                  <div className="mb-[15px] grid gap-[10px]">
                    {appeal.user && (
                      <>
                        <Button onClick={showBanModal} className="max-h-[45px]">
                          {appeal.user.is_banned
                            ? "Разблокировать пользователя"
                            : "Заблокировать пользователя"}
                        </Button>
                        <Button
                          onClick={showBonusesModal}
                          className="max-h-[45px]"
                        >
                          Начислить баллы
                        </Button>
                      </>
                    )}
                    {appeal.is_finished ? (
                      <Button
                        className="max-h-[45px]"
                        disabled={isReopeningTicket}
                        onClick={handleReopenAppeal}
                      >
                        Открыть тикет заново
                      </Button>
                    ) : (
                      <Button
                        className="max-h-[45px]"
                        disabled={isClosingTicket}
                        onClick={handleCloseAppeal}
                      >
                        Закрыть тикет
                      </Button>
                    )}
                    <Button
                      className="max-h-[45px]"
                      mode="darkBorder"
                      onClick={showReportModal}
                    >
                      Отправить разработчику
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <InformationBlock className="col-span-2" />
            )}
          </QueryLayout>
        ) : activeOrderId ? (
          <>
            <div className="grid grid-rows-[40px,1fr] gap-[10px] overflow-y-hidden">
              <Title className="flex items-center">Детали заказа</Title>
              <OrderItemDetails readOnly orderId={activeOrderId} />
            </div>
            <div className="grid grid-rows-[40px,90px,1fr,auto] gap-[10px] overflow-y-hidden">
              <Title className="flex items-center">
                Информация о заказчике
              </Title>
              {orderAccountId ? (
                <UserInformation userId={orderAccountId} />
              ) : (
                <InformationBlock>
                  У заказа нет привязанного пользователя
                </InformationBlock>
              )}
              {orderUser && (
                <div className="mb-[15px] grid gap-[10px]">
                  <Button
                    onClick={showOrderUserBanModal}
                    className="max-h-[45px]"
                  >
                    {orderUser.is_banned
                      ? "Разблокировать пользователя"
                      : "Заблокировать пользователя"}
                  </Button>
                  <Button
                    onClick={showOrderUserBonusesModal}
                    className="max-h-[45px]"
                  >
                    Начислить баллы
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          <InformationBlock className="col-span-2" />
        )}
      </>
    );
  })
);
